import React from 'react';
import { PostHogProvider, usePostHog } from 'posthog-js/react';

const EventsProvider = ({ children }: React.PropsWithChildren<object>): JSX.Element => (
    <PostHogProvider
        apiKey="phc_vKtZGbccj0B6BTDB4wqtIJ19l87De4wAB43MvXJpoj7"
        options={{
            api_host: `${process.env.GATSBY_BASE_URL}/ph-q`,
            defaults: '2025-05-24',
        }}
    >
        {children}
    </PostHogProvider>
);

type CaptureGoal = (eventName: string, eventData: Record<string, string>) => void;

export const useGoal = (): CaptureGoal => {
    const posthog = usePostHog();

    return React.useCallback((eventName, eventData) => {
        posthog?.capture(eventName, eventData);
    }, [posthog]);
};

export enum FeatureFlag {
    Control = 'control',
    Variant = 'variant',
    WaitingForBucketing = 'waiting',
}

type UseFlag = {
    flag: FeatureFlag;
    wasIgnored: boolean;
    debug?: {
        posthog: { ready: boolean; value: string | boolean | null | undefined };
    };
};

const interpretPostHog = (value: string | boolean | null | undefined): FeatureFlag | 'waiting' => {
    if (value === undefined || value === null) return 'waiting';
    if (typeof value === 'boolean') return value ? FeatureFlag.Variant : FeatureFlag.Control;
    return value.toLowerCase() === 'variant' ? FeatureFlag.Variant : FeatureFlag.Control;
};

const IGNORE_PREFIX = 'flag.ignore.';

export const useFlag = (flagName: string): UseFlag => {
    const posthog = usePostHog();
    const ignoreKey = `${IGNORE_PREFIX}${flagName}`;
    const [ignored, setIgnored] = React.useState<boolean>(false);

    const [phReady, setPhReady] = React.useState(false);
    const [phValue, setPhValue] = React.useState<string | boolean | null | undefined>(undefined);
    const phSubscribed = React.useRef(false);

    React.useEffect(() => {
        try {
            const wasIgnored = localStorage.getItem(ignoreKey);
            if (wasIgnored) {
                setIgnored(true);
                return;
            }
            // eslint-disable-next-line no-empty
        } catch (e) {
        }

        if (!posthog) {
            setPhReady(false);
            setPhValue(undefined);
            return;
        }

        try {
            setPhValue(posthog.getFeatureFlag?.(flagName));
        } catch {
            setPhValue(undefined);
        }

        if (!phSubscribed.current && posthog.onFeatureFlags) {
            phSubscribed.current = true;
            posthog.onFeatureFlags(() => {
                try {
                    setPhValue(posthog.getFeatureFlag?.(flagName));
                    setPhReady(true);
                } catch {
                    setPhValue(undefined);
                    setPhReady(true);
                }
            });
        }
    }, [posthog, flagName, ignoreKey]);

    const phInterp = interpretPostHog(phValue);
    const posthogFlag = (() => {
        if (ignored) return FeatureFlag.Control;
        if (phInterp === 'waiting' || !phReady) {
            return FeatureFlag.WaitingForBucketing;
        }
        return phInterp;
    })();

    if (ignored) {
        return {
            flag: FeatureFlag.Control,
            wasIgnored: ignored,
            debug: {
                posthog: { ready: phReady, value: phValue },
            },
        };
    }

    if (posthogFlag === FeatureFlag.WaitingForBucketing) {
        return {
            flag: FeatureFlag.WaitingForBucketing,
            wasIgnored: ignored,
            debug: {
                posthog: { ready: phReady, value: phValue },
            },
        };
    }

    return {
        flag: posthogFlag,
        wasIgnored: ignored,
        debug: {
            posthog: { ready: phReady, value: phValue },
        },
    };
};

export default EventsProvider;
